// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/clerkShim';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/Page/PageSkeletons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Eye, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import useRegistrationTeamStore from '@/store/registrationTeamStore';
import { normalizeStatus, getStatusLabel } from '@staty/shared/utils/registrationTeamStatus';

interface RegistrationTeam {
  _id: string;
  teamName: string;
  status: string;
  teamManagers: any[];
  teamRepresentatives: any[];
  players: any[];
  created: string;
  updated: string;
  season?: string;
  division?: string;
  team?: string;
}

const RegistrationTeamAdmin: React.FC = () => {
  const { registrationId } = useParams<{ registrationId: string }>();
  const { getToken } = useAuth();
  const navigate = useNavigate();
  
  const { 
    registrationTeams, 
    getRegistrationTeamsByRegistrationId 
  } = useRegistrationTeamStore();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (registrationId) {
        try {
          const token = await getToken();
          await getRegistrationTeamsByRegistrationId(registrationId, token);
        } catch (error) {
          console.error('Error fetching registration teams:', error);
          toast.error('Failed to load teams data');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [registrationId, getRegistrationTeamsByRegistrationId, getToken]);

  // Admin-set status, NOT a capacity/waitlist state — see
  // packages/shared/utils/waitlist.ts:getCapacityState. Labels come from the
  // shared option list so this page and RegistrationAdmin can't drift; the old
  // hand-rolled 'pending'/'rejected' cases described values nothing ever wrote.
  const getStatusBadge = (status: string) => {
    const normalized = normalizeStatus(status);
    const label = getStatusLabel(normalized);
    switch (normalized) {
      case 'accepted':
        return <Badge variant="default" className="bg-success">{label}</Badge>;
      case 'waitlisted':
        return <Badge variant="outline" className="text-warning border-warning/30 bg-warning/10">{label}</Badge>;
      case 'withdrawn':
        return <Badge variant="destructive">{label}</Badge>;
      default:
        return <Badge variant="secondary">{label}</Badge>;
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeaderSkeleton />
        <TableSkeleton columns={7} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Registration Teams"
        description="Manage team registrations"
        actions={(
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/registration/${registrationId}`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Registration
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Teams ({registrationTeams.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {registrationTeams.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No teams registered yet
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Team Name</TableHead>
                  <TableHead>Admin Status</TableHead>
                  <TableHead>Managers</TableHead>
                  <TableHead>Representatives</TableHead>
                  <TableHead>Players</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrationTeams.map((team: RegistrationTeam) => (
                  <TableRow key={team._id}>
                    <TableCell className="font-medium">{team.teamName}</TableCell>
                    <TableCell>{getStatusBadge(team.status)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {team.teamManagers?.length || 0} managers
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {team.teamRepresentatives?.length || 0} representatives
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {team.players?.length || 0} players
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {team.created ? new Date(team.created).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          asChild
                        >
                          <Link to={`/admin/registration/${registrationId}/team/${team._id}/managers`}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Managers
                          </Link>
                        </Button>
                        {(team.league || team.season) && team.division && team.team ? (
                          <Button
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <Link to={`/admin/organization/league/${team.league || team.season}/division/${team.division}/team/${team.team}/players`}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Players
                            </Link>
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Players
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default RegistrationTeamAdmin;
